/**
 * Labstep
 *
 * @module services/postFilter
 * @desc Business logic for Post Filter
 */

import type {
  EntityViewParameters,
  PostFilterComparisonType,
  PostFilterNodeType,
} from 'labstep-web/services/postFilter/constants';
import {
  PostFilterComparison,
  PostFilterOperator,
} from 'labstep-web/services/postFilter/constants';
import { QueryParameterService } from 'labstep-web/services/query-parameter.service';

export class PostFilter<
  TAttribute extends string,
  TPath extends string,
> {
  private readonly predicates: (
    | [TAttribute, PostFilterComparisonType]
    | PostFilter<TAttribute, TPath>
  )[];
  private readonly path: TPath | undefined;

  /**
   * Check if two nodes point at the same filter
   * @param nodeA Node to compare
   * @param nodeB Node to compare
   * @returns Are the nodes the same
   */
  public static compareNodes(
    nodeA: PostFilterNodeType,
    nodeB: PostFilterNodeType,
  ): boolean {
    if (nodeA.path !== nodeB.path) {
      return false;
    }

    if (nodeA.path === 'metadatas' && nodeB.path === 'metadatas') {
      if (
        nodeA.predicates!.length < 2 ||
        nodeB.predicates!.length < 2 ||
        !QueryParameterService.isPostFilterNodeType(
          nodeA.predicates![0],
        ) ||
        !QueryParameterService.isPostFilterNodeType(
          nodeA.predicates![1],
        ) ||
        !QueryParameterService.isPostFilterNodeType(
          nodeB.predicates![0],
        ) ||
        !QueryParameterService.isPostFilterNodeType(
          nodeB.predicates![1],
        )
      ) {
        return false;
      }
      return (
        nodeA.predicates![0].attribute ===
          nodeB.predicates![0].attribute &&
        nodeA.predicates![0].comparison ===
          nodeB.predicates![0].comparison &&
        nodeA.predicates![0].value === nodeB.predicates![0].value &&
        nodeA.predicates![1].attribute ===
          nodeB.predicates![1].attribute
      );
    }

    return nodeA.predicates!.every((predA, i) => {
      const predB = nodeB.predicates![i];
      return (
        QueryParameterService.isPostFilterNodeType(predA) &&
        QueryParameterService.isPostFilterNodeType(predB) &&
        predA.attribute === predB.attribute &&
        (predA.comparison === predB.comparison ||
          predA.comparison === PostFilterComparison.null ||
          predB.comparison === PostFilterComparison.null)
      );
    });
  }

  /**
   * Creates a post filter
   * Predicates are an array of tuples where the first value is the attribute
   * and the second value is the comparison
   * e.g. [['name', PostFilterComparison.eq]]
   * The path is optional and will be added to the node if provided
   *
   * You can use the getNode method to get a node to add to the post filter
   * and pass in values to add to the predicates
   * e.g. postFilter.getNode(['test'])
   *
   * @param predicates Predicates to add to the post filter
   * @param path Path of the post filter
   */
  public constructor(
    predicates: (
      | [TAttribute, PostFilterComparisonType]
      | PostFilter<TAttribute, TPath>
    )[],
    path?: TPath,
  ) {
    this.predicates! = predicates;
    this.path = path;
  }

  /**
   * Checks if a node matches the post filter
   * @param node Node to check
   * @param compareValue Optional value to compare against
   * @param isNested If the node is nested, e.g. in a PostFilter
   * @returns Is the node active
   */
  public isNodeActive(
    node: PostFilterNodeType,
    compareValues?: string[],
    isNested: boolean = false,
    comparisonCheck: boolean = false,
  ): boolean {
    if (this.path && node.path !== this.path) {
      return false;
    }

    if (
      !isNested &&
      node.predicates!.some(
        (p) =>
          typeof p === 'object' && p !== null && 'predicates' in p,
      )
    ) {
      return false;
    }

    const isPostFilter = (
      p: any,
    ): p is PostFilter<TAttribute, TPath> => p instanceof PostFilter;

    return this.predicates!.every((filterPred, i) => {
      const nodePred = node.predicates![i];

      if (isPostFilter(filterPred)) {
        // Recursively check nested node
        if (!nodePred || !('predicates' in nodePred)) {
          return false;
        }
        return filterPred.isNodeActive(
          nodePred as PostFilterNodeType,
          compareValues,
          true,
          comparisonCheck,
        );
      } else if (nodePred && Array.isArray(filterPred)) {
        if (!QueryParameterService.isPostFilterNodeType(nodePred)) {
          return false;
        }
        const [attribute, comparison] = filterPred;
        if (
          nodePred.comparison === PostFilterComparison.null ||
          comparison === PostFilterComparison.null
        ) {
          return (
            nodePred.attribute === attribute &&
            nodePred.comparison === comparison
          );
        }
        return (
          nodePred.attribute === attribute &&
          (!comparisonCheck || nodePred.comparison === comparison) &&
          (!compareValues || nodePred.value === compareValues[i])
        );
      }
      return false;
    });
  }

  /**
   * Get the node from the search parameters if active
   * @param params Search params
   * @param compareValues Optional value to compare against
   * @returns Is the node in the post filter
   */
  public getNodeInParams(
    params: EntityViewParameters,
    compareValues?: string[],
  ): PostFilterNodeType | undefined {
    if (!params.filter) {
      return undefined;
    }
    const predicatesUser = params.filter[0].predicates;
    if (!predicatesUser) {
      return undefined;
    }

    // Handle both legacy and new format for system filters
    const systemFilter = params.filter[1] as any;
    let predicatesSystem: PostFilterNodeType[] = [];

    if (systemFilter) {
      if ('path' in systemFilter && !('type' in systemFilter)) {
        // Legacy format: single PostFilterNodeType
        predicatesSystem = [systemFilter];
      } else if (
        'type' in systemFilter &&
        'predicates' in systemFilter
      ) {
        // New format: { type, predicates }
        predicatesSystem = systemFilter.predicates!;
      }
    }

    return [...predicatesUser, ...predicatesSystem].find((node) =>
      this.isNodeActive(node, compareValues, true),
    );
  }

  /**
   * Returns the comparison and value of attributes of a node
   * e.g. { name: { comparison: PostFilterComparison.eq, value: 'test' } }
   * If the node does not match the post filter, an empty object is returned
   * @param node Node to get attribute values from
   * @returns A record of attribute comparisons and values
   */
  public getAttributeValues(
    node: PostFilterNodeType,
  ):
    | Record<
        TAttribute,
        { comparison: PostFilterComparisonType; value: string }
      >
    | Record<string, never> {
    if (!this.isNodeActive(node, undefined, true)) {
      return {};
    }

    // Helper to recursively extract all attribute values from the node tree
    function extractAll(
      predicates: PostFilterNodeType[],
    ): Record<
      string,
      { comparison: PostFilterComparisonType; value: string }
    > {
      return predicates.reduce(
        (acc, predicate) => {
          if (
            QueryParameterService.isPostFilterNodeType(predicate) &&
            predicate.attribute &&
            predicate.comparison
          ) {
            acc[predicate.attribute] = {
              comparison: predicate.comparison,
              value: predicate.value as any,
            };
          } else if (
            predicate.predicates &&
            Array.isArray(predicate.predicates)
          ) {
            Object.assign(acc, extractAll(predicate.predicates));
          }
          return acc;
        },
        {} as Record<
          string,
          { comparison: PostFilterComparisonType; value: string }
        >,
      );
    }

    return extractAll(node.predicates!) as Record<
      TAttribute,
      { comparison: PostFilterComparisonType; value: string }
    >;
  }

  /**
   * Returns the path of the post filter
   * @returns Path
   */
  public getPath(): TPath | undefined {
    return this.path;
  }

  public getAllPaths(): (TPath | string | undefined)[] {
    // Helper to recursively collect paths
    const collectPaths = (
      filter: PostFilter<any, any>,
    ): (string | undefined)[] => {
      const paths: (string | undefined)[] = [];
      if (filter.path !== undefined) {
        paths.push(filter.path);
      }
      for (const pred of filter.predicates!) {
        if (pred instanceof PostFilter) {
          paths.push(...collectPaths(pred));
        }
      }
      return paths;
    };

    return collectPaths(this);
  }

  /**
   * Returns a node to add to the post filter
   * Values are optional and will be added to the predicates if provided
   * @param values Values to add to the predicates.
   * If a value is a tuple, the first value is the value and the second value is the comparison
   * e.g. ['test', PostFilterComparison.eq]
   * If a value is a string, the comparison will be the default comparison.
   * e.g. 'test'
   * @returns Node
   */
  public getNode(
    values?: (
      | string
      | [string, PostFilterComparisonType]
      | undefined
    )[],
  ): PostFilterNodeType {
    return {
      path: this.path,
      type: PostFilterOperator.and,
      predicates: this.predicates!.map((predicate, i) => {
        if (predicate instanceof PostFilter) {
          return predicate.getNode(values);
        } else {
          const [attribute, defaultComparison] = predicate;
          let comparison = defaultComparison;
          let value: string | undefined;
          if (values) {
            const valueAtIndex = values[i];
            if (Array.isArray(valueAtIndex)) {
              [value, comparison] = valueAtIndex;
            } else {
              value = valueAtIndex;
            }
          }
          return {
            attribute,
            comparison,
            value,
          };
        }
      }),
    };
  }

  /**
   * Returns the predicates of the post filter
   * Each predicate is a tuple of attribute, comparison and path
   * e.g. [['name', PostFilterComparison.eq, 'metadata']]
   * If the post filter contains nested PostFilter instances, their predicates will be flattened
   * @returns An array of tuples with attribute and comparison
   * */
  public getPredicates(): [
    TAttribute,
    PostFilterComparisonType,
    TPath | undefined,
  ][] {
    const flatten = (
      preds: (
        | PostFilter<TAttribute, TPath>
        | [TAttribute, PostFilterComparisonType]
      )[],
      currentPath: TPath | undefined,
    ): [TAttribute, PostFilterComparisonType, TPath | undefined][] =>
      preds.flatMap((p) =>
        Array.isArray(p)
          ? [[p[0], p[1], currentPath]]
          : flatten(p.predicates!, p.path),
      );
    return flatten(this.predicates!, this.path);
  }
}
